import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, CheckCircle, Clock, Video, MessageSquare, Sparkles, Zap, Film, Users, Star } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionHeader from "@/components/SectionHeader";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// Interactive Problem/Solution Toggle Card Component
const ProblemSolutionCard = ({ 
  icon: Icon, 
  problem, 
  consequence,
  solutionIcon: SolutionIcon,
  solution,
  solutionDescription,
  isOpen,
  onToggle
}: { 
  icon: any; 
  problem: string; 
  consequence: string;
  solutionIcon: any;
  solution: string;
  solutionDescription: string;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <motion.div 
    className={`group p-4 md:p-6 rounded-2xl border transition-all duration-500 cursor-pointer ${
      isOpen 
        ? 'bg-gradient-to-br from-accent/10 to-accent/5 border-accent/30' 
        : 'bg-red-500/5 border-red-500/30 md:hover:border-red-500/50'
    }`}
    onClick={onToggle}
    whileHover={{ scale: 1.01 }}
    layout
  >
    <div className="flex items-start gap-3 md:gap-4">
      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
        isOpen ? 'bg-accent' : 'bg-red-500/20'
      }`}>
        {isOpen ? (
          <SolutionIcon className="w-5 h-5 md:w-6 md:h-6 text-white" />
        ) : (
          <Icon className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className={`font-medium text-sm md:text-base transition-colors duration-300 ${isOpen ? 'text-accent' : 'text-foreground'}`}>
            {isOpen ? solution : problem}
          </h3>
          <div className={`w-8 h-5 md:w-10 md:h-6 rounded-full flex items-center p-0.5 transition-all duration-300 flex-shrink-0 ${
            isOpen ? 'bg-accent justify-end' : 'bg-red-400 justify-start'
          }`}>
            <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-white shadow-sm" />
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.p 
            key={isOpen ? 'solution' : 'problem'}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
            className="text-xs md:text-sm text-muted-foreground mt-1"
          >
            {isOpen ? solutionDescription : consequence}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  </motion.div>
);

const problemsWithSolutions = [
  {
    icon: Video,
    problem: "Videoqualität reicht nicht aus",
    consequence: "Unprofessionelle Produktionen schaden dem Markenimage.",
    solutionIcon: Film,
    solution: "Cinematische Hollywood-Ästhetik",
    solutionDescription: "Professionelles Equipment und erfahrenes Team für maximale Wirkung."
  },
  {
    icon: Clock,
    problem: "Lange Produktionszeiten",
    consequence: "Projekte dauern oft Monate und kosten Zeit und Energie.",
    solutionIcon: Zap,
    solution: "KI-Produktionen in 7 Tagen",
    solutionDescription: "Schnelle Videoproduktion ohne Drehtag – je nach Aufwand in unter 2 Wochen fertig."
  },
  {
    icon: Users,
    problem: "Keine Kundenstimmen",
    consequence: "Ohne Testimonials fehlt Vertrauen bei potenziellen Kunden.",
    solutionIcon: Star,
    solution: "Emotionale Testimonials",
    solutionDescription: "Kundenstories, die Vertrauen schaffen und Conversions steigern."
  },
  {
    icon: AlertCircle,
    problem: "Generischer Content",
    consequence: "Stock-Videos lassen euch in der Masse untergehen.",
    solutionIcon: Sparkles,
    solution: "Einzigartige Markenfilme",
    solutionDescription: "Maßgeschneiderte Videos, die eure Werte transportieren und Marke stärken."
  },
];

const ProblemsAndSolutions = () => {
  const [openProblems, setOpenProblems] = useState<number[]>([]);
  const isMobile = useIsMobile();

  const toggleProblem = (index: number) => {
    setOpenProblems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-background via-background to-background relative overflow-hidden">
      {/* Grid texture background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px),
                          linear-gradient(to bottom, hsl(var(--foreground)) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }}></div>
      
      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }}></div>

      {/* Subtle background glow */}
      <div className="absolute inset-0 opacity-5">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full transition-colors duration-700 blur-[120px] ${
          openProblems.length > 0 ? "bg-accent" : "bg-red-500"
        }`}></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
        <SectionHeader
          badge="Herausforderungen"
          title="Ihre Herausforderungen"
          highlightedWord="Herausforderungen"
          description="Tippen Sie auf eine Karte, um die Lösung zu sehen"
          className="mb-10 md:mb-14"
        />

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {problemsWithSolutions.map((item, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <ProblemSolutionCard 
                {...item} 
                isOpen={openProblems.includes(index)}
                onToggle={() => toggleProblem(index)}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Toggle all button */}
        <div className="text-center mt-6">
          <button 
            onClick={() => setOpenProblems(openProblems.length === problemsWithSolutions.length ? [] : problemsWithSolutions.map((_, i) => i))}
            className="text-xs md:text-sm text-muted-foreground hover:text-accent transition-colors"
          >
            {openProblems.length === problemsWithSolutions.length ? 'Alle zurücksetzen' : 'Alle Lösungen anzeigen'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProblemsAndSolutions;
